// Server-Sent Events consumer for /api/generate.
// We use fetch() + ReadableStream rather than EventSource because we need
// POST + cookie credentials, which EventSource does not support.

export type EmailStyle = 'luxury' | 'playful' | 'direct' | 'minimal' | 'bold';
export type GenerationMode = 'brief' | 'format';
export type CopyPolicy = 'preserve' | 'polish' | 'rewrite';
export type AttachmentPurpose = 'summarize' | 'reference' | 'extract_offers';
export type AttachmentKind = 'image' | 'pdf' | 'text' | 'markdown' | 'csv';

export interface GenerateAttachment {
  id: string;
  name: string;
  mime: string;
  kind: AttachmentKind;
  purpose: AttachmentPurpose;
  summary: string;
  facts: string[];
  design_cues?: string[];
  extracted_text_preview?: string;
  image_url?: string;
}

export interface GenerateInput {
  mode?: GenerationMode;
  prompt?: string;
  source_content?: string;
  copy_policy?: CopyPolicy;
  attachments?: GenerateAttachment[];
  style: EmailStyle;
  language?: string;
  starter_template_slug?: string | null;
  starter_html?: string | null;
  template_context?: GenerateTemplateContext | null;
}

export interface GenerateTemplateContext {
  template_id: string;
  template_label: string;
  template_category: string;
  audience_type?: string;
  campaign_goal?: string;
  campaign_type?: string;
  required_sections?: string[];
  optional_sections?: string[];
  default_cta_intent?: string;
  copy_density?: string;
  image_strategy?: string;
}

export interface GenerateMeta {
  subject: string;
  preview_text: string;
}

export interface GenerateDone {
  draft_id: string;
  version: number;
  subject: string;
  preview_text: string;
}

export interface GenerateHandlers {
  onMeta?: (meta: GenerateMeta) => void;
  onChunk?: (chunk: string) => void;
  onDone?: (result: GenerateDone) => void;
  onError?: (message: string) => void;
}

export async function streamGenerate(
  input: GenerateInput,
  handlers: GenerateHandlers,
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch('/api/generate', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body: JSON.stringify(input),
    signal,
  });

  if (!res.ok || !res.body) {
    let msg = `Request failed (${res.status})`;
    try {
      const data = (await res.json()) as { error?: string; message?: string };
      msg = data.message || data.error || msg;
    } catch {
      /* ignore */
    }
    handlers.onError?.(msg);
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let sepIdx: number;
      while ((sepIdx = buffer.indexOf('\n\n')) !== -1) {
        const raw = buffer.slice(0, sepIdx);
        buffer = buffer.slice(sepIdx + 2);
        const event = parseSseEvent(raw);
        if (!event) continue;
        switch (event.event) {
          case 'meta':
            handlers.onMeta?.(event.data as GenerateMeta);
            break;
          case 'html_chunk':
            handlers.onChunk?.((event.data as { chunk: string }).chunk);
            break;
          case 'done':
            handlers.onDone?.(event.data as GenerateDone);
            return;
          case 'error':
            handlers.onError?.((event.data as { message?: string }).message ?? 'Generation failed');
            return;
        }
      }
    }
  } catch (e) {
    if ((e as Error).name === 'AbortError') return;
    handlers.onError?.((e as Error).message ?? 'Stream error');
  }
}

// ---------------------------------------------------------------------------
// Demo SSE consumer (POST /api/generate/demo) — public, IP-throttled, no D1
// ---------------------------------------------------------------------------

export interface GenerateDemoInput {
  prompt: string;
  style: EmailStyle;
  language?: string;
}

export interface GenerateDemoDone {
  subject: string;
  preview_text: string;
  html: string;
  remaining: number;
  limit: number;
}

export interface GenerateDemoErrorPayload {
  message: string;
  /** When the server hit the daily IP cap, used by the UI to show "0/3 left". */
  used?: number;
  limit?: number;
  /** Best-effort error code mirrored from the JSON envelope. */
  code?: string;
}

export interface GenerateDemoHandlers {
  onMeta?: (meta: GenerateMeta) => void;
  onChunk?: (chunk: string) => void;
  onDone?: (result: GenerateDemoDone) => void;
  onError?: (err: GenerateDemoErrorPayload) => void;
}

export async function streamGenerateDemo(
  input: GenerateDemoInput,
  handlers: GenerateDemoHandlers,
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch('/api/generate/demo', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body: JSON.stringify(input),
    signal,
  });

  if (!res.ok || !res.body) {
    let payload: GenerateDemoErrorPayload = { message: `Request failed (${res.status})` };
    try {
      const data = (await res.json()) as {
        error?: string;
        message?: string;
        used?: number;
        limit?: number;
      };
      payload = {
        message: data.message || data.error || payload.message,
        used: data.used,
        limit: data.limit,
        code: data.error,
      };
    } catch {
      /* keep default message */
    }
    handlers.onError?.(payload);
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let sepIdx: number;
      while ((sepIdx = buffer.indexOf('\n\n')) !== -1) {
        const raw = buffer.slice(0, sepIdx);
        buffer = buffer.slice(sepIdx + 2);
        const event = parseSseEvent(raw);
        if (!event) continue;
        switch (event.event) {
          case 'meta':
            handlers.onMeta?.(event.data as GenerateMeta);
            break;
          case 'html_chunk':
            handlers.onChunk?.((event.data as { chunk: string }).chunk);
            break;
          case 'done':
            handlers.onDone?.(event.data as GenerateDemoDone);
            return;
          case 'error':
            handlers.onError?.({
              message: (event.data as { message?: string }).message ?? 'Generation failed',
            });
            return;
        }
      }
    }
  } catch (e) {
    if ((e as Error).name === 'AbortError') return;
    handlers.onError?.({ message: (e as Error).message ?? 'Stream error' });
  }
}

function parseSseEvent(raw: string): { event: string; data: unknown } | null {
  let event = 'message';
  let data = '';
  for (const line of raw.split('\n')) {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) data += line.slice(5).trim();
  }
  if (!data) return null;
  try {
    return { event, data: JSON.parse(data) };
  } catch {
    return { event, data };
  }
}

// ---------------------------------------------------------------------------
// Modify SSE consumer (POST /api/drafts/:id/modify)
// ---------------------------------------------------------------------------

export type ModifyMode = 'patch' | 'rewrite';

export interface ModifyDone {
  mode: ModifyMode;
  draft_id: string;
  version?: number;
  /** patch mode: full new HTML (server applies ops) */
  html?: string;
  /** patch mode with client-side apply: replacement HTML for selected node */
  replacement_html?: string;
  /** rewrite mode: subject + preview from regenerated META */
  subject?: string;
  preview_text?: string;
  summary?: string;
}

export interface ModifyHandlers {
  onMode?: (mode: ModifyMode) => void;
  onMeta?: (meta: GenerateMeta) => void;
  onChunk?: (chunk: string) => void;
  onDone?: (result: ModifyDone) => void;
  onError?: (message: string) => void;
}

export interface ModifyInput {
  modification_prompt: string;
  mode?: ModifyMode;
  client_apply?: boolean;
  selected_html?: string | null;
  base_version: number;
}

export async function streamModify(
  draftId: string,
  input: ModifyInput,
  handlers: ModifyHandlers,
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch(`/api/drafts/${draftId}/modify`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body: JSON.stringify(input),
    signal,
  });

  if (!res.ok || !res.body) {
    let msg = `Request failed (${res.status})`;
    try {
      const data = (await res.json()) as { error?: string; message?: string };
      msg = data.message || data.error || msg;
    } catch {
      /* ignore */
    }
    handlers.onError?.(msg);
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let sepIdx: number;
      while ((sepIdx = buffer.indexOf('\n\n')) !== -1) {
        const raw = buffer.slice(0, sepIdx);
        buffer = buffer.slice(sepIdx + 2);
        const event = parseSseEvent(raw);
        if (!event) continue;
        switch (event.event) {
          case 'mode':
            handlers.onMode?.((event.data as { mode: ModifyMode }).mode);
            break;
          case 'meta':
            handlers.onMeta?.(event.data as GenerateMeta);
            break;
          case 'html_chunk':
            handlers.onChunk?.((event.data as { chunk: string }).chunk);
            break;
          case 'done':
            await handlers.onDone?.(event.data as ModifyDone);
            return;
          case 'error':
            handlers.onError?.(
              (event.data as { message?: string }).message ?? 'Modify failed',
            );
            return;
        }
      }
    }
  } catch (e) {
    if ((e as Error).name === 'AbortError') return;
    handlers.onError?.((e as Error).message ?? 'Stream error');
  }
}
